import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Users, MessageCircle, BookOpen, Database, UserPlus, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold font-['Outfit']">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      try {
        const [statsRes, usersRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/users`),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data.users);
      } catch (err) {
        console.error("Admin fetch error:", err);
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [isAuthenticated]);

  if (loading || fetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 page-transition" data-testid="admin-dashboard">
      <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-8">Admin Dashboard</h1>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="admin-stats-grid">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} accent="bg-primary/10 text-primary" />
          <StatCard icon={UserPlus} label="New Users (7d)" value={stats.new_users_7d} accent="bg-green-500/10 text-green-600" />
          <StatCard icon={MessageCircle} label="Questions Asked" value={stats.total_questions} accent="bg-blue-500/10 text-blue-600" />
          <StatCard icon={BookOpen} label="Practice Problems" value={stats.total_practice} accent="bg-orange-500/10 text-orange-600" />
          <StatCard icon={FileText} label="Solutions Saved" value={stats.total_solutions} accent="bg-purple-500/10 text-purple-600" />
          <StatCard icon={Database} label="Cached Responses" value={stats.total_cached} accent="bg-slate-500/10 text-slate-600" />
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-xl">Registered Users</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-3 pr-4 font-medium">Name</th>
                <th className="pb-3 pr-4 font-medium">Email</th>
                <th className="pb-3 pr-4 font-medium hidden sm:table-cell">Questions</th>
                <th className="pb-3 pr-4 font-medium hidden md:table-cell">Practice</th>
                <th className="pb-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 pr-4 font-medium">{u.name}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{u.email}</td>
                  <td className="py-3 pr-4 hidden sm:table-cell">{u.stats?.total_questions ?? 0}</td>
                  <td className="py-3 pr-4 hidden md:table-cell">{u.stats?.practice_completed ?? 0}</td>
                  <td className="py-3 text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">No users registered yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
